import ComputerIcon from '@mui/icons-material/Computer';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {Container, Divider, FormHelperText, ToggleButton, Tooltip} from '@mui/material';
import {ReactElement} from 'react';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {
  SingleChoiceToggleButtonInputField,
  SingleChoiceToggleButtonInputFieldProps,
} from '../input/SingleChoiceToggleButtonInputField';
import {ComponentWithoutChildren} from '../react/types';
import {useThemeName} from './hooks/useThemeName';
import {NamespacePrefixedKey} from '#common/i18n/types';
import {ThemeName} from '#common/theme/themes';

export type ThemeToggleProps = Omit<SingleChoiceToggleButtonInputFieldProps, 'children' | 'onChange' | 'value'>;

interface ThemeLabel {
  icon: ReactElement<unknown>;
  translationKey: NamespacePrefixedKey;
}
export const ThemeLabels: Record<ThemeName, ThemeLabel> = {
  dark: {icon: <DarkModeIcon />, translationKey: 'main:theme.dark'},
  light: {icon: <LightModeIcon />, translationKey: 'main:theme.light'},
  system: {icon: <ComputerIcon />, translationKey: 'main:theme.system'},
};

export const ThemeToggle: ComponentWithoutChildren<ThemeToggleProps> = ({...toggleProps}: ThemeToggleProps) => {
  const {setThemeName} = useThemeName();
  const {translate} = useTranslation('main');
  const theme = translate('main:theme');
  return (
    <>
      <SingleChoiceToggleButtonInputField
        {...toggleProps}
        onChange={(_event, value: ThemeName) => {
          setThemeName(value);
        }}
        required
      >
        {Object.keys(ThemeLabels).map((value) => {
          const themeLabel = translate(ThemeLabels[value as ThemeName].translationKey);
          return (
            <ToggleButton aria-label={`${themeLabel} ${theme}`} key={value} value={value}>
              <Container>
                <Tooltip arrow title={themeLabel}>
                  {ThemeLabels[value as ThemeName].icon}
                </Tooltip>
                <Divider orientation="horizontal" />
                {themeLabel}
              </Container>
            </ToggleButton>
          );
        })}
      </SingleChoiceToggleButtonInputField>
      <FormHelperText sx={{ml: '14px'}}>{theme}</FormHelperText>
    </>
  );
};
